import { useState, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, User, Mail, Phone, Briefcase, MapPin, Calendar } from "lucide-react";
import EditProfileModal from "../components/EditProfileModal";
import { LoginContext } from "../LoginContext";
import { useTranslation } from "react-i18next";
import { ProfileUpdateContext } from "./ProfileLayout";
// useTranslation also used in InfoItem component below
import useProfile from "../hooks/useProfile";

export default function ProfileDetails() {
  const [showEditModal, setShowEditModal] = useState(false);
  const { loginInfo } = useContext(LoginContext);
  const { t } = useTranslation();
  const loggedInUser = loginInfo?.username;
  const profileContext = useContext(ProfileUpdateContext);
  const queryClient = useQueryClient();

  const { data: profile, isLoading: loading, isError } = useProfile(loggedInUser);

  const getInitials = (profile) => {
    if (!profile) return "U";
    return [profile.firstName, profile.lastName].filter(Boolean).map(n => n[0]).join("").toUpperCase();
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f7699]"></div>
      <span className="ml-3 text-slate-500 dark:text-slate-400 font-medium">{t("profile.loadingProfile", "Lade Profil...")}</span>
    </div>
  );

  if (isError || !profile) return (
    <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-2xl border border-red-100 dark:border-red-800 text-red-700 dark:text-red-300 text-center font-medium">
      {t("profile.noProfileFound", "Kein Profil gefunden.")}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <User size={20} className="sm:hidden" /><User size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profile.profileDetails", "Profil Details")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profile.loggedInAs", "Angemeldet als:")} <span className="font-bold">{loginInfo?.username}</span>
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 md:p-10">
          <div className="space-y-6 sm:space-y-8 md:space-y-12">
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-12 gap-y-4 sm:gap-y-6 md:gap-y-8">
                <InfoItem icon={<User size={14}/>} label={t("profile.firstName", "Vorname")} value={profile.firstName} />
                <InfoItem icon={<User size={14}/>} label={t("profile.lastName", "Nachname")} value={profile.lastName} />
                <InfoItem icon={<Mail size={14}/>} label={t("profile.emailAddress", "E-Mail")} value={profile.email} />
                <InfoItem icon={<Phone size={14}/>} label={t("profile.phoneNumber", "Telefon")} value={profile.phone} />
                <InfoItem icon={<Briefcase size={14}/>} label={t("profile.company", "Unternehmen")} value={profile.company} />
                <InfoItem icon={<MapPin size={14}/>} label={t("profile.billingAddress", "Rechnungsadresse")} value={profile.billingAddress} />
                <InfoItem icon={<Calendar size={14}/>} label={t("profile.accountCreated", "Konto erstellt")} value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : null} />
              </div>

              <div className="mt-6 sm:mt-8 md:mt-12 pt-4 sm:pt-6 md:pt-8 border-t border-slate-100 dark:border-slate-700">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="inline-flex items-center gap-3 px-5 sm:px-8 h-11 sm:h-14 bg-[#0f7699] text-white rounded-2xl font-bold text-xs sm:text-sm tracking-widest shadow-xl shadow-blue-900/10 hover:bg-[#0d6482] transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Edit size={18} />
                  <span>{t("profile.editProfile", "Profil bearbeiten")}</span>
                </button>
              </div>
            </div>
          </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <EditProfileModal
          onClose={() => setShowEditModal(false)}
          onSave={(updatedProfile) => {
            // Update React Query cache
            queryClient.setQueryData(["profile", loggedInUser], updatedProfile);

            // Also update context if still used by other components
            if (profileContext?.updateProfile) {
              profileContext.updateProfile(updatedProfile);
            }
          }}
        />
      )}
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-[#0f7699] dark:text-[#4fc3f7] opacity-70">
        {icon}
        <span className="text-[10px] font-black uppercase tracking-widest">
          {label}
        </span>
      </div>
      <p className="text-[15px] font-bold text-slate-700 dark:text-slate-200">
        {value || t("profile.notSpecified", "Nicht angegeben")}
      </p>
    </div>
  );
}
